import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tryLogout, TRY_LOGOUT } from '@/actions/login';
import { getError, getIsLoading, getUniqueIntegrationProvidersForVCSUserData, userIsRunwayAdmin } from '@/helpers';
import { displayTextForPermission, displayTextForUserGroup } from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Layout from 'antd/lib/layout/layout';
import Descriptions from 'antd/lib/descriptions';
import Button from 'antd/lib/button';
import { globalMessage } from '@/utils/globalMessage';
import Divider from 'antd/lib/divider';
import { generateUserSettingsLink, RegisterDeviceMobileAlert } from '../RegisterDeviceAlert';
import { useIsIOSDevice, useIsMobile } from '@/mediaQueriesHelpers';
import { DeviceDetailsDrawer, ShortUDIDComponent } from '../appSettings/ProfilesAndDevices/DeviceDetailsDrawer';
import { Device, IntegrationProvider } from '@/types';
import List from 'antd/lib/list';
import Dropdown from 'antd/lib/dropdown';
import {
    DownOutlined,
    EditOutlined,
    InfoCircleOutlined,
    MailOutlined,
    PlusOutlined,
    QrcodeOutlined,
    TabletOutlined,
} from '@ant-design/icons';
import Space from 'antd/lib/space';
import Modal from 'antd/lib/modal';
import { QRCode } from 'react-qrcode-logo';
import RunwayAdminSettings from './RunwayAdminSettings';
import { RegisterDeviceInstructionsSheet } from '../RegisterDeviceInstructions';
import {
    selectAllSupportedVCSIntegrationTypes,
    selectOrg,
    selectOrgGroups,
    selectOrgProviders,
    selectUserDevices,
} from '@/selectors/selectors';
import { ManualAddDeviceModal } from '../appSettings/ProfilesAndDevices/ManualAddDeviceModal';
import { AddUserDeviceRequest } from '@/actions/appSettings';
import { tryUpdateUserDevices } from '@/actions/user';
import EditVCSUserNameModal from './EditVCSUserNameModal';
import { UserUpdatable } from '@/actions/updatable';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { Tabs } from 'antd/lib';
import EmailNotificationSettings from './EmailNotificationSettings';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import Tooltip from 'antd/lib/tooltip';
import { useCommsUserIdVerification } from '../setup/hooks/useCommsUserIdVerification';

export enum UserSettingsPageView {
    Email = 'email',
    Devices = 'devices',
}

const UserSettingsComponent: React.FC = () => {
    const orgId = useSelector((state: RootState) => selectOrg(state).id);
    const userId = useSelector((state: RootState) => state.user.id);
    const user = useSelector((state: RootState) => state.user);
    const devices = useSelector((state: RootState) => selectUserDevices(state));
    const groups = useSelector((state: RootState) => selectOrgGroups(state, orgId));
    const [highlightedDevice, setHighlightedDevice] = React.useState<Device | null>(null);
    const [qrCodeLink, setQRCodeLink] = React.useState<string | null>(null);
    const [isRegisterDeviceInstructionsVisible, setIsRegisterDeviceInstructionsVisible] = React.useState(false);
    const [shouldShowManualAddDeviceModal, setShouldShowManualAddDeviceModal] = React.useState(false);
    const userSettingsLink = generateUserSettingsLink();
    const [shouldShowEditVCSUserNameModal, setShouldShowEditVCSUserNameModal] = React.useState(false);
    const [selectedTab, setSelectedTab] = React.useState(UserSettingsPageView.Devices);
    const [shouldPreopenRegisterDeviceAlert, setShouldPreopenRegisterDeviceAlert] = React.useState(false);

    // user device requests & responses
    const addUserDeviceError = useError(UserUpdatable.UPDATE_USER_DEVICES, userId);
    const isAddingDevice = useIsLoading(UserUpdatable.UPDATE_USER_DEVICES, userId);
    const addUserDeviceSuccess = useUpdated(UserUpdatable.UPDATE_USER_DEVICES, userId);
    const logoutIsLoading = useSelector((state: RootState) => getIsLoading(TRY_LOGOUT, state));
    const logoutError = useSelector((state: RootState) => getError(TRY_LOGOUT, state));
    const isMobile = useIsMobile();
    const isIos = useIsIOSDevice();
    const location = useLocation();
    const history = useHistory();
    const dispatch = useDispatch();

    const searchParams = qs.parse(location.search);

    useCommsUserIdVerification();

    React.useEffect(() => {
        const shouldPreopenRegisterDeviceAlertFromParams = searchParams?.registerDevice === 'true';
        const unsubscribeSuccess = searchParams?.unsubscribeSuccess === 'true';
        const unsubscribeError = searchParams?.unsubscribeSuccess === 'false';
        const selectedTabFromParams = searchParams?.selectedTab;

        let needsHistoryReplace = false;

        if (selectedTabFromParams) {
            setSelectedTab(selectedTabFromParams as UserSettingsPageView);
            needsHistoryReplace = true;
        }

        if (shouldPreopenRegisterDeviceAlertFromParams) {
            setShouldPreopenRegisterDeviceAlert(true);
            needsHistoryReplace = true;
        }

        if (unsubscribeSuccess) {
            globalMessage.success('Successfully unsubscribed!');
            needsHistoryReplace = true;
        }

        if (unsubscribeError) {
            globalMessage.error('Unsubscribe failed. Please try again.');
            needsHistoryReplace = true;
        }

        if (needsHistoryReplace) {
            history.replace(window.location.pathname);
        }
    }, [searchParams]);

    React.useEffect(() => {
        if (addUserDeviceError) {
            globalMessage.error(addUserDeviceError);
        }
    }, [addUserDeviceError]);

    React.useEffect(() => {
        if (addUserDeviceSuccess) {
            globalMessage.success('Device added successfully!');
            setShouldShowManualAddDeviceModal(false);
        }
    }, [addUserDeviceSuccess]);

    const logout = () => {
        dispatch(tryLogout());
    };

    React.useEffect(() => {
        if (logoutError) {
            globalMessage.error(logoutError);
        }
    }, [logoutError]);

    const isRunwayAdmin = userIsRunwayAdmin(user);

    const shouldShowRegisterDeviceAlert = devices.length === 0 && isMobile && isIos;

    const addDevicesButton = () => {
        return (
            <div
                style={{
                    marginTop: 12,
                    display: 'flex',
                    gap: 12,
                    justifyContent: 'right',
                }}
            >
                <Dropdown
                    menu={{
                        items: [
                            {
                                label: 'Register on device from QR code',
                                key: '1',
                                icon: (
                                    <QrcodeOutlined
                                        style={{
                                            color: runwayColors.primary,
                                        }}
                                    />
                                ),
                                onClick: () => {
                                    setQRCodeLink(`${window.location.origin}${userSettingsLink}`);
                                },
                            },
                            {
                                label: 'Register manually',
                                key: '2',
                                icon: (
                                    <EditOutlined
                                        style={{
                                            color: runwayColors.primary,
                                        }}
                                    />
                                ),
                                onClick: () => {
                                    setShouldShowManualAddDeviceModal(true);
                                },
                            },
                        ],
                    }}
                >
                    <Button type="primary" icon={<PlusOutlined />}>
                        <Space
                            style={{
                                marginLeft: 4,
                            }}
                        >
                            <Text
                                style={{
                                    color: runwayColors.white,
                                }}
                            >
                                Register a personal device
                            </Text>
                            <DownOutlined />
                        </Space>
                    </Button>
                </Dropdown>
            </div>
        );
    };

    const orgProviders = useSelector((state: RootState) => selectOrgProviders(state, orgId));

    const allSupportedVCSIntegrationTypes = useSelector(selectAllSupportedVCSIntegrationTypes);

    const uniqueVCSProviders = getUniqueIntegrationProvidersForVCSUserData(
        orgProviders,
        user.appIds ?? [],
        allSupportedVCSIntegrationTypes ?? [],
    );

    const EmailNotificationsView = () => {
        return (
            <div
                style={{
                    height: 'fit-content',
                }}
            >
                <EmailNotificationSettings appId={userId} orgId={orgId} />
            </div>
        );
    };

    const DevicesView = () => {
        return (
            <>
                <div>{!isMobile && <div>{addDevicesButton()}</div>}</div>
                <div style={{ color: antGrey['grey8'] }}>
                    <>
                        {shouldShowRegisterDeviceAlert && (
                            <RegisterDeviceMobileAlert
                                isInline
                                isClosable={false}
                                shouldShowActionButton
                                shouldStartOpen={shouldPreopenRegisterDeviceAlert}
                            />
                        )}

                        {!shouldShowRegisterDeviceAlert && (
                            <>
                                <List
                                    dataSource={devices ?? []}
                                    split={false}
                                    className="drawer-list"
                                    bordered
                                    locale={{
                                        emptyText: 'No devices yet.',
                                    }}
                                    renderItem={(device) => {
                                        return (
                                            <List.Item
                                                onClick={() => {
                                                    setHighlightedDevice(device);
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: antGrey['grey2'],
                                                    marginBottom: 2,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Text
                                                    ellipsis
                                                    style={{
                                                        color: antGrey['grey8'],
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    {device.displayName}
                                                </Text>
                                                <ShortUDIDComponent udid={device.udid} />
                                            </List.Item>
                                        );
                                    }}
                                />
                                {isMobile && (
                                    <Button
                                        type="link"
                                        style={{
                                            color: runwayColors.primary,
                                        }}
                                        onClick={() => {
                                            setIsRegisterDeviceInstructionsVisible(true);
                                        }}
                                        icon={<PlusOutlined />}
                                    >
                                        Register another personal device
                                    </Button>
                                )}
                            </>
                        )}
                    </>
                </div>
            </>
        );
    };
    const tabItems = [
        {
            key: UserSettingsPageView.Email,
            label: (
                <>
                    <MailOutlined />
                    Email Notifications
                </>
            ),
            children: <EmailNotificationsView />,
        },
        {
            key: UserSettingsPageView.Devices,
            label: (
                <>
                    <TabletOutlined />
                    My Devices
                </>
            ),
            children: <DevicesView />,
        },
    ];

    return (
        <Layout
            style={{
                padding: 24,
                position: 'relative',
                overflow: 'scroll',
                height: 0,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                <div
                    style={{
                        maxWidth: 1200,
                    }}
                >
                    <div>
                        <Text style={titleStyle}>User settings</Text>
                        <Descriptions
                            layout={isMobile ? 'vertical' : 'horizontal'}
                            size="middle"
                            bordered
                            column={1}
                            style={{ marginTop: 24 }}
                            labelStyle={{ width: 200 }}
                        >
                            <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
                            <Descriptions.Item label="Permissions">
                                {user.permissions.length > 0 &&
                                    user.permissions
                                        .map((permission) => displayTextForPermission(permission))
                                        .join(', ')}
                                {user.permissions.length === 0 && <Text type="secondary">No permissions</Text>}
                            </Descriptions.Item>
                            <Descriptions.Item label="User Groups">
                                {user.groups &&
                                    user.groups.length > 0 &&
                                    user.groups.map((groupId) => displayTextForUserGroup(groups[groupId])).join(', ')}
                                {user.groups && user.groups.length === 0 && (
                                    <Text type="secondary">No user groups</Text>
                                )}
                            </Descriptions.Item>
                            {uniqueVCSProviders.map((vcs: IntegrationProvider) => {
                                const vcsUserName =
                                    !!user.vcsUserDataByProvider && !!user.vcsUserDataByProvider[vcs.id] ?
                                        user.vcsUserDataByProvider[vcs.id].username
                                    :   '';
                                return (
                                    <Descriptions.Item
                                        key={vcs.id}
                                        label={
                                            <div style={{ position: 'relative', width: '100%' }}>
                                                <span>{vcs.name} user name</span>
                                                <Tooltip title={'Provider ID: ' + vcs.id} placement="right">
                                                    <InfoCircleOutlined
                                                        style={{
                                                            color: runwayColors.info,
                                                            position: 'absolute',
                                                            right: 0,
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                        }}
                                                    />
                                                </Tooltip>
                                            </div>
                                        }
                                    >
                                        <div>
                                            <Text style={{ verticalAlign: 'middle' }}>{vcsUserName}</Text>
                                            <Button
                                                style={{ float: 'right' }}
                                                onClick={() => {
                                                    setShouldShowEditVCSUserNameModal(true);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <EditVCSUserNameModal
                                                open={shouldShowEditVCSUserNameModal}
                                                onCancel={function (): void {
                                                    setShouldShowEditVCSUserNameModal(false);
                                                }}
                                                orgId={orgId}
                                                userId={userId}
                                                vcsUserName={vcsUserName}
                                                vcsProviderId={vcs.id}
                                                vcsName={vcs.name}
                                            />
                                        </div>
                                    </Descriptions.Item>
                                );
                            })}
                        </Descriptions>
                    </div>

                    <Divider />

                    <div
                        style={{
                            marginTop: 12,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 12,
                            }}
                        >
                            <Tabs
                                destroyInactiveTabPane
                                activeKey={selectedTab}
                                items={tabItems}
                                onChange={(activeKey: string) => setSelectedTab(activeKey as UserSettingsPageView)}
                            />
                        </div>
                        <DeviceDetailsDrawer
                            device={highlightedDevice}
                            onClose={() => setHighlightedDevice(null)}
                            orgId={orgId}
                            shouldShowRegistrationStatus={false}
                        />
                        <Modal open={!!qrCodeLink} onCancel={() => setQRCodeLink(null)} destroyOnClose footer={null}>
                            <div
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <QRCode value={qrCodeLink} size={420} />
                            </div>
                        </Modal>
                        <RegisterDeviceInstructionsSheet
                            isOpen={isRegisterDeviceInstructionsVisible}
                            onClose={() => {
                                setIsRegisterDeviceInstructionsVisible(false);
                            }}
                        />
                        <div>
                            {isRunwayAdmin && (
                                <div
                                    style={{
                                        marginBottom: 24,
                                    }}
                                >
                                    <Divider />
                                    <RunwayAdminSettings />
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        style={{
                            flex: 1,
                        }}
                    />

                    <div style={{ marginTop: 24, padding: '20px 0' }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Button onClick={logout} loading={logoutIsLoading}>
                                Sign out
                            </Button>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginTop: 12,
                            }}
                        >
                            <Text type="secondary" style={{ textAlign: 'right' }}>
                                Version {PACKAGE_VERSION}
                            </Text>
                        </div>
                    </div>
                </div>
                <ManualAddDeviceModal
                    title="Register a personal device"
                    isVisible={shouldShowManualAddDeviceModal}
                    isLoading={isAddingDevice}
                    onSubmit={(values) => {
                        const request: AddUserDeviceRequest = {
                            orgId,
                            userId,
                            ...values,
                        };
                        dispatch(tryUpdateUserDevices(request));
                    }}
                    onCancel={() => {
                        setShouldShowManualAddDeviceModal(false);
                    }}
                />
            </div>
        </Layout>
    );
};

export default UserSettingsComponent;
